import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createReceiving,
  fetchInventoryItems,
  fetchPendingPurchaseOrders,
  fetchReceivingPurchaseOrders,
} from '../services/receivingService';
import { subscribeToPurchaseOrderChanges } from '../services/purchaseOrderRealtimeService';

const initialItemValues = {
  poCode: '',
  scaleSlipNo: '',
  mark: '',
  description: '',
  numberOfSacks: '',
  weigher: '',
  representative: '',
  inboundWeightKg: '',
  inboundDate: '',
  inboundTime: '',
  inboundPlateNumber: '',
  outboundDate: '',
  outboundTime: '',
  outboundPlateNumber: '',
  grossKg: '',
  tareKg: '',
  netKg: '',
  dryQuantityKg: '0',
  wetQuantityKg: '0',
  rejectQuantityKg: '0',
  moistureContent: '0',
  remarks: '',
};

const receiptAutofillFieldNames = [
  'scaleSlipNo',
  'mark',
  'description',
  'numberOfSacks',
  'weigher',
  'representative',
  'inboundWeightKg',
  'inboundDate',
  'inboundTime',
  'inboundPlateNumber',
  'outboundDate',
  'outboundTime',
  'outboundPlateNumber',
  'grossKg',
  'tareKg',
  'netKg',
];
const stepTwoDefaultZeroFieldNames = [
  'dryQuantityKg',
  'wetQuantityKg',
  'rejectQuantityKg',
  'moistureContent',
];
const stepTwoDecimalFieldNames = ['dryQuantityKg', 'wetQuantityKg', 'rejectQuantityKg'];
const receiptNumericFieldNames = [
  'numberOfSacks',
  'inboundWeightKg',
  'grossKg',
  'tareKg',
  'netKg',
];
const initialFilterValues = {
  supplierName: '',
  date: '',
  status: 'Initial + In Progress',
};

const RECEIVING_PAGE_SIZE = 10;
const RECEIVING_VISIBLE_STATUSES = ['Initial', 'In Progress', 'Received', 'Completed'];
const RECEIVING_INITIAL_ACTIVE_STATUS_FILTER = 'Initial + In Progress';
const RECEIVING_INITIAL_ACTIVE_STATUSES = ['Initial', 'In Progress'];

function useReceiving(options = {}) {
  const includeUnlockedInventory = Boolean(options.includeUnlockedInventory);
  const inventoryOnly = Boolean(options.inventoryOnly);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [receivingOrders, setReceivingOrders] = useState([]);
  const [filterValues, setFilterValues] = useState(initialFilterValues);
  const [appliedFilters, setAppliedFilters] = useState(initialFilterValues);
  const [currentPage, setCurrentPage] = useState(1);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState('');
  const [itemValues, setItemValues] = useState(initialItemValues);
  const [receiptFieldErrors, setReceiptFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const selectedPurchaseOrder = useMemo(
    () =>
      pendingOrders.find(
        (purchaseOrder) => String(purchaseOrder.id) === String(selectedPurchaseOrderId)
      ) ||
      receivingOrders.find(
        (purchaseOrder) => String(purchaseOrder.id) === String(selectedPurchaseOrderId)
      ) || null,
    [pendingOrders, receivingOrders, selectedPurchaseOrderId]
  );

  const filteredReceivingOrders = useMemo(() => {
    const normalizedSearch = appliedFilters.supplierName.trim().toLowerCase();

    return receivingOrders.filter((purchaseOrder) => {
      if (!RECEIVING_VISIBLE_STATUSES.includes(purchaseOrder.status)) {
        return false;
      }

      const matchesSearch =
        !normalizedSearch ||
        getReceivingSearchableCode(purchaseOrder).toLowerCase().includes(normalizedSearch) ||
        String(purchaseOrder.supplierName || '').toLowerCase().includes(normalizedSearch) ||
        String(purchaseOrder.id).includes(normalizedSearch) ||
        String(purchaseOrder.sacksMark || '').toLowerCase().includes(normalizedSearch) ||
        String(purchaseOrder.lansaName || '').toLowerCase().includes(normalizedSearch);

      const matchesDate =
        !appliedFilters.date || toDateInputValue(purchaseOrder.arrivalDate) === appliedFilters.date;

      const matchesStatus = doesReceivingStatusMatchFilter(
        purchaseOrder.status,
        appliedFilters.status
      );

      return matchesSearch && matchesDate && matchesStatus;
    });
  }, [appliedFilters, receivingOrders]);
  const totalReceivingOrders = filteredReceivingOrders.length;
  const totalPages = Math.max(1, Math.ceil(totalReceivingOrders / RECEIVING_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * RECEIVING_PAGE_SIZE;
  const endIndex = Math.min(startIndex + RECEIVING_PAGE_SIZE, totalReceivingOrders);
  const paginatedReceivingOrders = useMemo(
    () => filteredReceivingOrders.slice(startIndex, endIndex),
    [endIndex, filteredReceivingOrders, startIndex]
  );
  const receivingSearchSuggestions = useMemo(
    () => buildReceivingSearchSuggestions(receivingOrders),
    [receivingOrders]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters]);

  const loadReceivingData = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }

    setLoadError('');

    try {
      const inventoryRequest = fetchInventoryItems({ includeUnlocked: includeUnlockedInventory });

      if (inventoryOnly) {
        const inventoryResponse = await inventoryRequest;

        setInventoryItems(inventoryResponse);
        return inventoryResponse;
      }

      const [pendingResponse, receivingOrdersResponse, inventoryResponse] = await Promise.all([
        fetchPendingPurchaseOrders(),
        fetchReceivingPurchaseOrders(),
        inventoryRequest,
      ]);

      setPendingOrders(pendingResponse);
      setReceivingOrders(receivingOrdersResponse);
      setInventoryItems(inventoryResponse);

      if (!selectedPurchaseOrderId && pendingResponse[0]) {
        setSelectedPurchaseOrderId(String(pendingResponse[0].id));
      }

      return inventoryResponse;
    } catch (error) {
      setLoadError(error.message);
      return [];
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, [includeUnlockedInventory, inventoryOnly, selectedPurchaseOrderId]);

  useEffect(() => {
    loadReceivingData();
  }, [loadReceivingData]);

  useEffect(() => {
    return subscribeToPurchaseOrderChanges(() => {
      loadReceivingData(false);
    });
  }, [loadReceivingData]);

  function handlePurchaseOrderChange(event) {
    selectPurchaseOrder(event.target.value);
  }

  function selectPurchaseOrder(purchaseOrderId) {
    const normalizedPurchaseOrderId = String(purchaseOrderId || '');
    const nextPurchaseOrder =
      pendingOrders.find(
        (purchaseOrder) => String(purchaseOrder.id) === normalizedPurchaseOrderId
      ) ||
      receivingOrders.find(
        (purchaseOrder) => String(purchaseOrder.id) === normalizedPurchaseOrderId
      );

    setSelectedPurchaseOrderId(normalizedPurchaseOrderId);
    setItemValues({
      ...initialItemValues,
      ...getReceivingDraftItemValues(nextPurchaseOrder),
      poCode: nextPurchaseOrder ? getReceivingSearchableCode(nextPurchaseOrder) : '',
    });
    setSubmitError('');
    setSuccessMessage('');
  }

  function handleItemChange(event) {
    const { name, value } = event.target;
    const nextValue = receiptNumericFieldNames.includes(name)
      ? cleanNumericInputValue(value)
      : value;

    if (stepTwoDecimalFieldNames.includes(name) && !/^\d*(?:\.\d*)?$/.test(nextValue)) {
      return;
    }

    if (name === 'moistureContent' && !/^\d*$/.test(nextValue)) {
      return;
    }

    setItemValues((currentValues) => {
      const nextValues = {
        ...currentValues,
        [name]: nextValue,
      };

      if (name === 'wetQuantityKg' && Number(nextValue || 0) <= 0) {
        nextValues.moistureContent = '0';
      }

      if (name === 'grossKg') {
        const grossKg = Number(String(nextValue || '').trim());
        const tareKg = Number(String(currentValues.tareKg || '').trim());

        if (Number.isFinite(grossKg) && Number.isFinite(tareKg)) {
          nextValues.netKg = formatComputedWeight(Math.max(0, grossKg - tareKg));
        }
      }

      if (name === 'inboundWeightKg') {
        nextValues.grossKg = nextValue;

        const grossKg = Number(String(nextValue || '').trim());
        const tareKg = Number(String(currentValues.tareKg || '').trim());

        if (Number.isFinite(grossKg) && Number.isFinite(tareKg)) {
          nextValues.netKg = formatComputedWeight(Math.max(0, grossKg - tareKg));
        }
      }

      return nextValues;
    });
    setReceiptFieldErrors((currentErrors) => ({
      ...currentErrors,
      [name]: '',
      ...(name === 'grossKg' || name === 'inboundWeightKg' ? { grossKg: '', netKg: '' } : {}),
    }));
    setSubmitError('');
    setSuccessMessage('');
  }

  function handleDefaultZeroBlur(event) {
    const { name, value } = event.target;

    if (!stepTwoDefaultZeroFieldNames.includes(name)) {
      return;
    }

    const normalizedValue = normalizeStepTwoZeroValue(value);

    if (normalizedValue === String(value || '').trim()) {
      return;
    }

    setItemValues((currentValues) => ({
      ...currentValues,
      [name]: normalizedValue,
    }));
    setSubmitError('');
    setSuccessMessage('');
  }

  function handleInboundWeightBlur(event) {
    const inboundWeight = String(event.target.value || '').trim();

    if (!inboundWeight) {
      return;
    }

    setItemValues((currentValues) => {
      const tareKg = Number(String(currentValues.tareKg || '').trim());
      const grossKg = Number(inboundWeight);
      const nextValues = {
        ...currentValues,
        grossKg: inboundWeight,
      };

      if (Number.isFinite(grossKg) && Number.isFinite(tareKg)) {
        nextValues.netKg = formatComputedWeight(Math.max(0, grossKg - tareKg));
      }

      return nextValues;
    });
    setReceiptFieldErrors((currentErrors) => ({
      ...currentErrors,
      grossKg: '',
      netKg: '',
    }));
    setSubmitError('');
    setSuccessMessage('');
  }

  function handleWeightComputationBlur() {
    setItemValues((currentValues) => {
      const grossKg = Number(String(currentValues.grossKg || '').trim());
      const tareKg = Number(String(currentValues.tareKg || '').trim());

      if (!Number.isFinite(grossKg) || !Number.isFinite(tareKg)) {
        return currentValues;
      }

      const netKg = Math.max(0, grossKg - tareKg);

      return {
        ...currentValues,
        netKg: formatComputedWeight(netKg),
      };
    });
    setReceiptFieldErrors((currentErrors) => ({
      ...currentErrors,
      netKg: '',
    }));
    setSubmitError('');
    setSuccessMessage('');
  }

  function handleFilterChange(event) {
    const { name, value } = event.target;
    const nextSuggestionStatus = getStatusFilterForReceivingSearchSuggestion(event.suggestion);

    setFilterValues((currentValues) => ({
      ...currentValues,
      [name]: value,
      ...(nextSuggestionStatus ? { status: nextSuggestionStatus } : {}),
    }));

    if (name === 'supplierName' || name === 'status') {
      setAppliedFilters((currentFilters) => ({
        ...currentFilters,
        [name]: value,
        ...(nextSuggestionStatus ? { status: nextSuggestionStatus } : {}),
      }));
      setCurrentPage(1);
    }
  }

  function handleFilterSubmit(event) {
    event.preventDefault();
    setAppliedFilters(filterValues);
    setCurrentPage(1);
  }

  function handleFilterReset() {
    setFilterValues(initialFilterValues);
    setAppliedFilters(initialFilterValues);
    setCurrentPage(1);
  }

  function handlePreviousPage() {
    setCurrentPage((page) => Math.max(page - 1, 1));
  }

  function handleNextPage() {
    setCurrentPage((page) => Math.min(page + 1, totalPages));
  }

  function applyReceiptAutofill(fields) {
    const normalizedFields = normalizeReceiptAutofillFields(fields);

    setItemValues((currentValues) => {
      const nextValues = { ...currentValues };

      receiptAutofillFieldNames.forEach((fieldName) => {
        if (typeof normalizedFields[fieldName] !== 'undefined') {
          nextValues[fieldName] = String(normalizedFields[fieldName] || '');
        }
      });

      setReceiptFieldErrors(buildReceiptFieldErrors(nextValues));

      return nextValues;
    });
    setSubmitError('');
    setSuccessMessage('');
  }

  function clearReceiptAutofillValues() {
    setItemValues((currentValues) => {
      const nextValues = { ...currentValues };

      receiptAutofillFieldNames.forEach((fieldName) => {
        nextValues[fieldName] = '';
      });

      return nextValues;
    });
    setReceiptFieldErrors({});
    setSubmitError('');
    setSuccessMessage('');
  }

  function addItem(receiptImageData = '') {
    if (!selectedPurchaseOrder) {
      setSubmitError('Select a purchase order before adding receiving items.');
      return false;
    }

    const dryQuantityValue = String(itemValues.dryQuantityKg || '').trim();
    const wetQuantityValue = String(itemValues.wetQuantityKg || '').trim();
    const rejectQuantityValue = String(itemValues.rejectQuantityKg || '').trim();
    const moistureContentValue = String(itemValues.moistureContent || '').trim();
    const quantityUnit = selectedPurchaseOrder.stockUnit === 'kg' ? 'kg' : 'sacks';
    const dryQuantityKg = Number(dryQuantityValue);
    const wetQuantityKg = Number(wetQuantityValue);
    const rejectQuantityKg = Number(rejectQuantityValue);
    const validationMessages = [];

    if (!isValidWholeNumberValue(moistureContentValue)) {
      validationMessages.push('Moisture content must be a valid number.');
    }

    if (
      !isValidStepTwoQuantityValue(dryQuantityValue) ||
      !isValidStepTwoQuantityValue(wetQuantityValue) ||
      !isValidStepTwoQuantityValue(rejectQuantityValue)
    ) {
      validationMessages.push('Dry, wet, and reject quantity must be valid numbers.');
    }

    if (dryQuantityKg + wetQuantityKg + rejectQuantityKg <= 0) {
      validationMessages.push('Enter a dry, wet, or reject quantity greater than 0.');
    }

    if (
      quantityUnit === 'sacks' &&
      (!Number.isInteger(dryQuantityKg) ||
        !Number.isInteger(wetQuantityKg))
    ) {
      validationMessages.push('Dry and wet quantities in sacks must be whole numbers.');
    }

    const requiredWeightFields = [['Net KG', itemValues.netKg]];
    requiredWeightFields.forEach(([label, value]) => {
      const numericValue = Number(String(value || '').trim());

      if (!Number.isFinite(numericValue) || numericValue <= 0) {
        validationMessages.push(`${label} must be greater than 0.`);
      }
    });

    if (validationMessages.length) {
      setSubmitError(validationMessages.join('\n'));
      return false;
    }

    const unitCost = Number(selectedPurchaseOrder.unitCost || 0);
    const receiptSummary = buildReceiptSummary(itemValues);
    const itemIdPrefix = Date.now();
    const netKg = Number(itemValues.netKg || 0);
    const totalReceivedSacks = dryQuantityKg + wetQuantityKg;
    const shouldUseNetKgCostBasis = quantityUnit === 'sacks' && netKg > 0;
    const dryCostQuantity = shouldUseNetKgCostBasis
      ? getAllocatedKgFromSacks(dryQuantityKg, totalReceivedSacks, netKg)
      : dryQuantityKg;
    const wetCostQuantity = shouldUseNetKgCostBasis
      ? getAllocatedKgFromSacks(wetQuantityKg, totalReceivedSacks, netKg)
      : wetQuantityKg;
    const rejectCostQuantity = rejectQuantityKg;
    const baseItem = {
      productName: 'Seaweed',
      moistureContent: formatMoistureContentValue(itemValues.moistureContent),
      unitCost,
      remarks: [itemValues.remarks.trim(), receiptSummary].filter(Boolean).join('\n\n'),
      receivedAt: new Date().toISOString(),
    };

    const nextItems = [
      dryQuantityKg >= 0
        ? {
            ...baseItem,
            id: `${itemIdPrefix}-dry`,
            category: 'Dry',
            quantity: dryQuantityKg,
            quantityUnit,
            quantityKg: dryCostQuantity,
            subtotal: dryCostQuantity * unitCost,
          }
        : null,
      wetQuantityKg >= 0
        ? {
            ...baseItem,
            id: `${itemIdPrefix}-wet`,
            category: 'Wet',
            quantity: wetQuantityKg,
            quantityUnit,
            quantityKg: wetCostQuantity,
            subtotal: wetCostQuantity * unitCost,
          }
        : null,
      rejectQuantityKg > 0
        ? {
            ...baseItem,
            id: `${itemIdPrefix}-reject`,
            category: 'Reject',
            quantity: quantityUnit === 'sacks' ? 0 : rejectQuantityKg,
            quantityUnit,
            quantityKg: rejectCostQuantity,
            rawNetKg: rejectCostQuantity,
            subtotal: rejectCostQuantity * unitCost,
          }
        : null,
    ].filter(Boolean);

    if (!nextItems.length) {
      setSubmitError('');
      return false;
    }

    if (receiptImageData && nextItems[0]) {
      nextItems[0].receiptImageData = receiptImageData;
    }

    setSubmitError('');
    return nextItems;
  }

  function cancelItem() {
    setItemValues(initialItemValues);
    setReceiptFieldErrors({});
    setSubmitError('');
    setSuccessMessage('');
  }

  async function submitReceiving(itemsToSubmit = []) {
    if (!selectedPurchaseOrder) {
      setSubmitError('Select a purchase order before submitting receiving.');
      return false;
    }

    if (!itemsToSubmit.length) {
      setSubmitError('Add at least one receiving item before submitting.');
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const isDraftSave = selectedPurchaseOrder.status === 'Initial';

      await createReceiving({
        purchaseOrderId: selectedPurchaseOrder.id,
        saveAsDraft: isDraftSave,
        items: itemsToSubmit.map(({ id, subtotal, ...item }) => item),
      });

      setSuccessMessage(
        isDraftSave ? 'Receiving draft saved successfully.' : 'Receiving submitted successfully.'
      );
      setItemValues(initialItemValues);
      setReceiptFieldErrors({});
      setSelectedPurchaseOrderId('');
      await loadReceivingData();
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  return {
    pendingOrders,
    receivingOrders,
    filteredReceivingOrders: paginatedReceivingOrders,
    filterValues,
    receivingPagination: {
      currentPage: normalizedPage,
      endItem: endIndex,
      isNextDisabled: normalizedPage >= totalPages,
      isPreviousDisabled: normalizedPage <= 1,
      onNext: handleNextPage,
      onPrevious: handlePreviousPage,
      startItem: totalReceivingOrders ? startIndex + 1 : 0,
      totalItems: totalReceivingOrders,
      totalPages,
    },
    receivingSearchSuggestions,
    inventoryItems,
    selectedPurchaseOrder,
    selectedPurchaseOrderId,
    itemValues,
    receiptFieldErrors,
    isLoading,
    isSaving,
    loadError,
    submitError,
    successMessage,
    refreshReceivingData: loadReceivingData,
    handlePurchaseOrderChange,
    selectPurchaseOrder,
    handleItemChange,
    handleDefaultZeroBlur,
    handleInboundWeightBlur,
    handleWeightComputationBlur,
    handleFilterChange,
    handleFilterSubmit,
    handleFilterReset,
    applyReceiptAutofill,
    clearReceiptAutofillValues,
    addItem,
    cancelItem,
    submitReceiving,
  };
}

function buildReceiptFieldErrors(values) {
  return receiptAutofillFieldNames.reduce((errors, fieldName) => {
    errors[fieldName] = '';

    return errors;
  }, {});
}

function normalizeReceiptAutofillFields(fields) {
  const normalizedFields = { ...(fields || {}) };
  normalizedFields.inboundPlateNumber = normalizePlateNumber(normalizedFields.inboundPlateNumber);
  normalizedFields.outboundPlateNumber = normalizePlateNumber(normalizedFields.outboundPlateNumber);
  normalizedFields.scaleSlipNo = cleanPositiveNumericValue(normalizedFields.scaleSlipNo);
  normalizedFields.numberOfSacks = cleanPositiveNumericValue(normalizedFields.numberOfSacks);
  normalizedFields.inboundWeightKg = cleanPositiveNumericValue(normalizedFields.inboundWeightKg);
  normalizedFields.inboundDate = normalizeSlipDateValue(normalizedFields.inboundDate);
  normalizedFields.outboundDate = normalizeSlipDateValue(normalizedFields.outboundDate);
  normalizedFields.grossKg = cleanPositiveNumericValue(normalizedFields.grossKg);
  normalizedFields.tareKg = cleanPositiveNumericValue(normalizedFields.tareKg);
  normalizedFields.netKg = cleanPositiveNumericValue(normalizedFields.netKg);
  normalizedFields.moistureContent = cleanWholeNumberValue(normalizedFields.moistureContent);

  return normalizedFields;
}

function formatMoistureContentValue(value) {
  const numericValue = String(value || '').trim();

  return numericValue ? `${numericValue}%` : '';
}

function isValidStepTwoQuantityValue(value) {
  return /^\d+(?:\.\d+)?$/.test(value);
}

function isValidWholeNumberValue(value) {
  return /^\d+$/.test(value);
}

function normalizeStepTwoZeroValue(value) {
  const normalizedValue = String(value || '').trim();

  return /^0+$/.test(normalizedValue) || !normalizedValue ? '0' : normalizedValue;
}

function getAllocatedKgFromSacks(itemSacks, totalSacks, netKg) {
  if (Number(totalSacks || 0) <= 0 || Number(netKg || 0) <= 0) {
    return 0;
  }

  return Math.round((Number(itemSacks || 0) / Number(totalSacks)) * Number(netKg) * 100) / 100;
}

function formatComputedWeight(value) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function cleanNumericValue(value) {
  return String(value || '')
    .toUpperCase()
    .replace(/[|!]/g, '1')
    .replace(/[IL]/g, '1')
    .replace(/[OQD]/g, '0')
    .replace(/S/g, '5')
    .replace(/B/g, '8')
    .replace(/[^\d.]/g, '');
}

function cleanNumericInputValue(value) {
  const normalizedValue = String(value || '')
    .toUpperCase()
    .replace(/[|!]/g, '1')
    .replace(/[IL]/g, '1')
    .replace(/[OQD]/g, '0')
    .replace(/S/g, '5')
    .replace(/B/g, '8')
    .replace(/[^\d.]/g, '');
  const [integerPart, ...decimalParts] = normalizedValue.split('.');

  return decimalParts.length ? `${integerPart}.${decimalParts.join('')}` : integerPart;
}

function cleanPositiveNumericValue(value) {
  const numericValue = cleanNumericValue(value);

  return Number(numericValue) > 0 ? numericValue : '';
}

function cleanWholeNumberValue(value) {
  const numericValue = String(value || '').replace(/[^\d]/g, '');

  return numericValue || '0';
}

function normalizePlateNumber(value) {
  const normalizedValue = String(value || '').trim().toUpperCase().replace(/\s+/g, ' ');
  const plateMatch = normalizedValue.match(/[A-Z]{2,4}\s*[-–—]\s*\d{3,5}/);

  if (plateMatch) {
    return plateMatch[0].replace(/[–—]/g, '-').replace(/\s*-\s*/g, '-');
  }

  return normalizedValue;
}

function normalizeSlipDateValue(value) {
  const normalizedValue = String(value || '').trim();

  if (!normalizedValue) {
    return '';
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
    return normalizedValue;
  }

  const dateMatch = normalizedValue.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);

  if (!dateMatch) {
    return '';
  }

  const month = Number(dateMatch[1]);
  const day = Number(dateMatch[2]);
  const rawYear = dateMatch[3];
  const year = Number(rawYear.length === 2 ? `20${rawYear}` : rawYear);

  if (!isValidLocalDate(year, month, day)) {
    return '';
  }

  return [
    String(year).padStart(4, '0'),
    String(month).padStart(2, '0'),
    String(day).padStart(2, '0'),
  ].join('-');
}

function isValidLocalDate(year, month, day) {
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function toDateInputValue(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toISOString().slice(0, 10);
}

function getReceivingSearchableCode(purchaseOrder) {
  if (purchaseOrder.poCode) {
    return String(purchaseOrder.poCode);
  }

  return `PO-${String(purchaseOrder.id).padStart(4, '0')}`;
}

function buildReceivingSearchSuggestions(purchaseOrders) {
  const suggestionsByKey = new Map();

  purchaseOrders.forEach((purchaseOrder) => {
    if (!RECEIVING_VISIBLE_STATUSES.includes(purchaseOrder.status)) {
      return;
    }

    const poCode = getReceivingSearchableCode(purchaseOrder);
    const supplierName = String(purchaseOrder.supplierName || '').trim();
    const sacksMark = String(purchaseOrder.sacksMark || '').trim();
    const lansaName = String(purchaseOrder.lansaName || '').trim();
    const status = purchaseOrder.status || '';

    if (poCode) {
      suggestionsByKey.set(`po-code:${poCode.toLowerCase()}`, {
        group: 'po-code',
        value: poCode,
        label: poCode,
        detail: supplierName ? `P.O. Code - ${supplierName}` : 'P.O. Code',
        status,
      });
    }

    if (supplierName) {
      suggestionsByKey.set(`supplier:${supplierName.toLowerCase()}:${status.toLowerCase()}`, {
        group: 'supplier',
        value: supplierName,
        label: supplierName,
        detail: status ? `Supplier - ${status}` : 'Supplier',
        status,
      });
    }

    if (sacksMark) {
      suggestionsByKey.set(`sacks-mark:${sacksMark.toLowerCase()}:${status.toLowerCase()}`, {
        group: 'sacks-mark',
        value: sacksMark,
        label: sacksMark,
        detail: [supplierName ? `Sacks Mark - ${supplierName}` : 'Sacks Mark', status]
          .filter(Boolean)
          .join(' - '),
        status,
      });
    }

    if (lansaName) {
      suggestionsByKey.set(`lansa:${lansaName.toLowerCase()}:${status.toLowerCase()}`, {
        group: 'lansa',
        value: lansaName,
        label: lansaName,
        detail: status ? `Lansa Name - ${status}` : 'Lansa Name',
        status,
      });
    }
  });

  return Array.from(suggestionsByKey.values()).sort((firstSuggestion, secondSuggestion) =>
    firstSuggestion.label.localeCompare(secondSuggestion.label)
  );
}

function getStatusFilterForReceivingSearchSuggestion(suggestion) {
  const status = suggestion?.status;

  return RECEIVING_VISIBLE_STATUSES.includes(status) ? status : '';
}

function doesReceivingStatusMatchFilter(status, filterStatus) {
  if (filterStatus === 'All') {
    return true;
  }

  if (filterStatus === RECEIVING_INITIAL_ACTIVE_STATUS_FILTER) {
    return RECEIVING_INITIAL_ACTIVE_STATUSES.includes(status);
  }

  return status === filterStatus;
}

function getReceivingDraftItemValues(purchaseOrder) {
  const items = Array.isArray(purchaseOrder?.receivingItems) ? purchaseOrder.receivingItems : [];

  if (!items.length) {
    return {};
  }

  const dryItem = findReceivingItemByCategory(items, 'Dry');
  const wetItem = findReceivingItemByCategory(items, 'Wet');
  const rejectItem = findReceivingItemByCategory(items, 'Reject');
  const detailSourceItem = items.find((item) => String(item.remarks || '').trim()) || items[0];
  const receiptFields = parseReceiptSummaryFields(detailSourceItem?.remarks || '');

  return {
    ...receiptFields,
    dryQuantityKg: formatDraftQuantityValue(dryItem?.quantity),
    wetQuantityKg: formatDraftQuantityValue(wetItem?.quantity),
    rejectQuantityKg: formatDraftQuantityValue(
      purchaseOrder?.stockUnit === 'sacks'
        ? rejectItem?.quantityKg || rejectItem?.rawNetKg
        : rejectItem?.quantity
    ),
    moistureContent: cleanWholeNumberValue(detailSourceItem?.moistureContent || '0'),
    remarks:
      purchaseOrder?.status === 'Initial'
        ? ''
        : extractReceivingRemarks(detailSourceItem?.remarks || ''),
  };
}

function extractReceivingRemarks(value) {
  const text = String(value || '').trim();
  const receiptMarkerIndex = text.indexOf('P.O. Code:');
  const remarks = receiptMarkerIndex >= 0 ? text.slice(0, receiptMarkerIndex).trim() : text;

  return remarks;
}

function findReceivingItemByCategory(items, category) {
  return items.find(
    (item) => String(item.category || '').toLowerCase() === String(category).toLowerCase()
  );
}

function formatDraftQuantityValue(value) {
  const numericValue = Number(value || 0);

  if (!Number.isFinite(numericValue)) {
    return '0';
  }

  return String(numericValue);
}

function parseReceiptSummaryFields(value) {
  const text = String(value || '').trim();
  const receiptMarkerIndex = text.indexOf('P.O. Code:');

  if (receiptMarkerIndex < 0) {
    return {};
  }

  const receiptText = text.slice(receiptMarkerIndex);
  const fieldNameByReceiptLabel = new Map([
    ['P.O. Code', 'poCode'],
    ['Scale Slip No.', 'scaleSlipNo'],
    ['Mark', 'mark'],
    ['Description', 'description'],
    ['No. of Sacks', 'numberOfSacks'],
    ['Weigher', 'weigher'],
    ['Representative', 'representative'],
    ['Inbound Date', 'inboundDate'],
    ['Inbound Time', 'inboundTime'],
    ['Inbound Plate No.', 'inboundPlateNumber'],
    ['Inbound Weight KG', 'inboundWeightKg'],
    ['Outbound Date', 'outboundDate'],
    ['Outbound Time', 'outboundTime'],
    ['Outbound Plate No.', 'outboundPlateNumber'],
    ['Gross KG', 'grossKg'],
    ['Tare KG', 'tareKg'],
    ['Net KG', 'netKg'],
  ]);

  return receiptText.split('\n').reduce((fields, line) => {
    const separatorIndex = line.indexOf(':');

    if (separatorIndex <= 0) {
      return fields;
    }

    const label = line.slice(0, separatorIndex).trim();
    const fieldName = fieldNameByReceiptLabel.get(label);

    if (!fieldName) {
      return fields;
    }

    return {
      ...fields,
      [fieldName]: line.slice(separatorIndex + 1).trim(),
    };
  }, {});
}

function buildReceiptSummary(values) {
  const receiptFields = [
    ['P.O. Code', values.poCode],
    ['Scale Slip No.', values.scaleSlipNo],
    ['Mark', values.mark],
    ['Description', values.description],
    ['No. of Sacks', values.numberOfSacks],
    ['Weigher', values.weigher],
    ['Representative', values.representative],
    ['Inbound Date', values.inboundDate],
    ['Inbound Time', values.inboundTime],
    ['Inbound Plate No.', values.inboundPlateNumber],
    ['Inbound Weight KG', values.inboundWeightKg],
    ['Outbound Date', values.outboundDate],
    ['Outbound Time', values.outboundTime],
    ['Outbound Plate No.', values.outboundPlateNumber],
    ['Gross KG', values.grossKg],
    ['Tare KG', values.tareKg],
    ['Net KG', values.netKg],
  ].filter(([, value]) => String(value || '').trim());

  if (!receiptFields.length) {
    return '';
  }

  return receiptFields
    .map(([label, value]) => `${label}: ${String(value).trim()}`)
    .join('\n');
}

export default useReceiving;
